import { useNavigation } from '@app/hooks/useNavigation';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { useKlausSyncEnabled } from '@dieterApi/company/useKlausLinkMutations';
import { Spinner } from '@components/ui';
import { useUserLogoutMutation } from '@dieterApi/user/useUserLogoutMutation.ts';
import { User, UserRole } from '@dieterApi/user/useUserQuery.ts';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Tooltip } from '@mui/material';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { ExpertModeModal } from './ExpertModeModal';
import { SuperExpertModeModal } from './SuperExpertModeModal';

interface IProps {
  onClose?(): void;
  user?: User | null;
  portalUrl?: string;
  loading?: boolean;
  onOpenManageSubscription?: () => void;
  onOpenChangelog?: () => void;
  onOpenDeleteAccount?: () => void;
  hasUnseenChangelog?: boolean;
}
function AccountMenuItems({
  onClose,
  user,
  portalUrl,
  loading,
  onOpenManageSubscription,
  onOpenChangelog,
  onOpenDeleteAccount,
  hasUnseenChangelog,
}: IProps) {
  const { t } = useTranslation();
  const { whitelabelConfig, whitelabelLoading, principalCompany, isDpoMode } = useUserWithCompany();
  // "Klaus-Verknüpfung" only where the Klaus coupling is set up.
  const klausSyncEnabled = useKlausSyncEnabled();
  const {
    setNavigation,
    navigation: { expertModeEnabled, superExpertModeEnabled },
  } = useNavigation();
  const [logout] = useUserLogoutMutation();
  const [expertModeModalOpen, setExpertModeModalOpen] = React.useState(false);
  const [superExpertModeModalOpen, setSuperExpertModeModalOpen] = React.useState(false);
  const showDieterItems = !whitelabelLoading && !whitelabelConfig;

  const currentMode = superExpertModeEnabled ? 'superExpert' : expertModeEnabled ? 'expert' : 'normal';

  const handleModeChange = (_: React.MouseEvent<HTMLElement>, newMode: string | null) => {
    if (newMode === null) return; // prevent deselecting all
    if (newMode === 'expert') {
      setExpertModeModalOpen(true);
    } else if (newMode === 'superExpert') {
      setSuperExpertModeModalOpen(true);
    } else {
      setNavigation((nav) => {
        nav.expertModeEnabled = false;
        nav.superExpertModeEnabled = false;
      });
    }
  };

  const [showWhitelabelConfig, setShowWhitelabelConfig] = React.useState(false);
  React.useEffect(() => {
    if (user?.companies) {
      if (principalCompany?.features?.includes('WhitelabelAdmin')) {
        setShowWhitelabelConfig(true);
      }
    }
  }, [user]);

  return (
    <>
      {!(!user || user.isAnonymous) && (
        <>
          <MenuItem
            onClick={onClose}
            component={NavLink}
            disabled={!user.mfaVerified}
            activeClassName="Mui-selected text-primary-root!"
            to="/userconfig"
          >
            {t('route.user-config.header')}
          </MenuItem>
          {showWhitelabelConfig && (
            <MenuItem
              onClick={onClose}
              component={NavLink}
              activeClassName="Mui-selected text-primary-root!"
              to="/whitelabel"
            >
              {t('route.whitelabel.config_ui', 'Whitelabel Configurator')}
            </MenuItem>
          )}

          <MenuItem
            onClick={onClose}
            component={NavLink}
            activeClassName="Mui-selected text-primary-root!"
            to="/usermanagement"
            disabled={
              (user.role !== UserRole.Admin &&
                user.role !== UserRole.Dpo &&
                !(user.role === UserRole.Editor && isDpoMode)) ||
              !user.mfaVerified
            }
          >
            {t('layout.global.header.account_dropdown_option.user_management')}
          </MenuItem>
          {klausSyncEnabled && (
            <MenuItem
              onClick={onClose}
              component="a"
              href="/zu-klaus?src=menu"
              target="_blank"
              rel="noopener"
              data-testid="account-menu-zu-klaus"
            >
              {t('layout.global.header.account_dropdown_option.zu_klaus', 'Zu Klaus')}
              <OpenInNewIcon fontSize="small" className="ml-2" />
            </MenuItem>
          )}
          {klausSyncEnabled && (
            <MenuItem
              onClick={onClose}
              component={NavLink}
              activeClassName="Mui-selected text-primary-root!"
              to="/klaus"
            >
              {t('layout.global.header.account_dropdown_option.klaus_link', 'Klaus-Verknüpfung')}
            </MenuItem>
          )}
          <MenuItem
            onClick={onClose}
            component={NavLink}
            activeClassName="Mui-selected text-primary-root!"
            to="/mfa"
            disabled={!user.mfaVerified}
          >
            {t('layout.global.header.account_dropdown_option.mfa')}
          </MenuItem>

          {showDieterItems && (
            <>
              <MenuItem
                component="a"
                onClick={onClose}
                href={portalUrl}
                rel="noopener noreferrer"
                disabled={!portalUrl || !user.mfaVerified}
                data-testid="button-account-menu-payment"
                className="flex justify-between gap-2"
              >
                {t('layout.global.header.account_dropdown_option.payment_settings', 'Zahlungseinstellungen')}
                {loading && <Spinner size="small" />}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  onOpenManageSubscription?.();
                  onClose?.();
                }}
              >
                {t('layout.global.header.account_dropdown_option.cancel_sub')}
              </MenuItem>
            </>
          )}

          <MenuItem
            onClick={() => {
              onOpenDeleteAccount?.();
              onClose?.();
            }}
            data-testid="button-account-menu-delete"
            disabled={!user.mfaVerified}
          >
            {t('layout.global.header.account_dropdown_option.delete_account', 'Account löschen')}
          </MenuItem>
          <Divider />
        </>
      )}
      <MenuItem disableRipple className="flex flex-col items-start! gap-1 py-2! cursor-default!">
        <span className="text-xs text-gray-500">
          {t('layout.global.header.account_dropdown_option.questionnaire_mode', 'Fragebogenmodus')}
        </span>
        <ToggleButtonGroup value={currentMode} exclusive onChange={handleModeChange} size="small" className="w-full">
          <ToggleButton value="normal" className="flex-1 text-xs! capitalize!">
            {t('layout.global.header.account_dropdown_option.normal_mode', 'Standard')}
          </ToggleButton>
          <Tooltip title={t('layout.global.header.account_dropdown_option.expert_mode_tooltip')} placement="top">
            <ToggleButton value="expert" className="flex-1 text-xs! capitalize!">
              {t('layout.global.header.account_dropdown_option.expert_mode')}
            </ToggleButton>
          </Tooltip>
          <Tooltip title={t('layout.global.header.account_dropdown_option.super_expert_mode_tooltip')} placement="top">
            <ToggleButton value="superExpert" className="flex-1 text-xs! capitalize!">
              {t('layout.global.header.account_dropdown_option.super_expert_mode')}
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </MenuItem>
      {showDieterItems && <Divider />}

      {showDieterItems && (
        <MenuItem
          onClick={() => {
            onOpenChangelog?.();
            onClose?.();
          }}
          className="flex justify-between items-center gap-4"
        >
          <span>{t('layout.global.header.account_dropdown_option.changelog', 'Neuigkeiten')}</span>
          {hasUnseenChangelog && <span className="size-2 rounded-full bg-red-500 shrink-0" />}
        </MenuItem>
      )}
      {showDieterItems && (
        <MenuItem onClick={onClose} component={NavLink} activeClassName="Mui-selected text-primary-root!" to="/terms">
          {t('layout.global.header.account_dropdown_option.terms', 'Rechtliches')}
        </MenuItem>
      )}
      {
        // Privacy link: if a whitelabelConfig with an absolute privacyUrl is present, use external <a>, otherwise route internally
        whitelabelConfig?.privacyUrl ? (
          <MenuItem
            component="a"
            onClick={onClose}
            href={whitelabelConfig.privacyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1"
          >
            {t('layout.global.header.account_dropdown_option.privacy')}
            <OpenInNewIcon fontSize="inherit" className="text-gray-400" />
          </MenuItem>
        ) : (
          <MenuItem
            component={NavLink}
            onClick={onClose}
            activeClassName="Mui-selected text-primary-root!"
            to="/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1"
          >
            {t('layout.global.header.account_dropdown_option.privacy')}
            <OpenInNewIcon fontSize="inherit" className="text-gray-400" />
          </MenuItem>
        )
      }
      {showDieterItems && (
        <MenuItem onClick={() => window.UC_UI.showSecondLayer()} data-testid="button-account-show-uc-settings">
          {t('layout.global.header.account_dropdown_option.privacy_settings')}
        </MenuItem>
      )}

      {showDieterItems && (
        <MenuItem
          component={NavLink}
          onClick={onClose}
          activeClassName="Mui-selected text-primary-root!"
          to="/imprint"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1"
        >
          {t('layout.global.header.account_dropdown_option.imprint', 'Impressum')}
          <OpenInNewIcon fontSize="inherit" className="text-gray-400" />
        </MenuItem>
      )}
      {!(!user || user.isAnonymous) && (
        <div>
          <Divider />
          <MenuItem onClick={() => logout()}>{t('common.action.logout', 'Ausloggen')}</MenuItem>
        </div>
      )}
      <ExpertModeModal open={expertModeModalOpen} onClose={() => setExpertModeModalOpen(false)} />
      <SuperExpertModeModal open={superExpertModeModalOpen} onClose={() => setSuperExpertModeModalOpen(false)} />
    </>
  );
}

export default AccountMenuItems;
